import React, { useState } from 'react';
import {
  Typography,
  Button,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { showReportDialog } from '@sentry/react';
import i18next from 'i18next';

import { ErrorActionsProps } from './types';

const useStyles = makeStyles({
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1rem',
    paddingTop: 0,
  },
  report: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'end',
    gap: '1rem',
    padding: '1rem',
    paddingTop: 0,
    '& > *': {
      flexGrow: 1,
    },
  },
});

type Props = ErrorActionsProps & {
  showReloadPageButton?: boolean;
  extraButtons?: JSX.Element;
};

function Actions({
  resetError,
  eventId,
  onBeforeReloadComponent,
  showReloadPageButton = true,
  extraButtons,
}: Props) {
  const { t } = useTranslation('Sentry');
  const styles = useStyles();
  const [isLoading, setLoading] = useState(false);

  function reloadComponent() {
    // If reloading does not resolve the issue at least give an impression that something did happen
    // Without the timeout it looks like button did not do anything
    setTimeout(resetError, 500);
  }

  function openReportDialog() {
    if (eventId)
      showReportDialog({
        eventId,
        title: i18next.t('Sentry:errorBoundary.dialog.title'),
        subtitle: i18next.t('Sentry:errorBoundary.dialog.subtitle'),
        subtitle2: i18next.t('Sentry:errorBoundary.dialog.subtitle2'),
      });
  }

  return (
    <section>
      <div className={styles.actions}>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={() => {
            setLoading(true);
            onBeforeReloadComponent?.();
            reloadComponent();
          }}
          endIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t('genericErrorBoundary.buttons.reloadComponent')}
        </Button>
        {showReloadPageButton ? (
          <>
            <Typography>{t('genericErrorBoundary.or')}</Typography>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => window.location.reload()}
            >
              {t('genericErrorBoundary.buttons.reloadPage')}
            </Button>
          </>
        ) : null}
        {extraButtons}
      </div>
      {eventId ? (
        <>
          <div className={styles.report}>
            <Typography>{t('genericErrorBoundary.reportInfo')}</Typography>
            <Button variant="outlined" onClick={() => openReportDialog()}>
              {t('genericErrorBoundary.buttons.openReportForm')}
            </Button>
          </div>
        </>
      ) : null}
    </section>
  );
}

export default Actions;
